import React, { useState, useEffect, useMemo, useRef } from 'react';
import { GUARDIAN_CLOCK_MISSING_MESSAGE, GUARDIAN_OVERDUE_PAY_MESSAGE, assessGuardianPayWindow } from '../utils/guardianEncounterDeadline';
import { soapGuardianEsignAccess } from '../utils/teletherapyEsign';
import { UserSession } from '../types';
import { ArcSearchInput } from './ArcSearchInput';
import {
  Calendar,
  Clock,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Search,
  PlusCircle,
  Edit2,
  FileText,
  Send,
  Check,
  X,
  RefreshCw,
  User,
  Filter,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  ExternalLink,
  MessageSquare,
  Lock,
  ChevronDown,
  ChevronUp,
  Building2,
  FileCheck2,
  Award,
  Target,
  Users
} from 'lucide-react';
import { DailyTreatmentNoteBody } from './clinical/DailyTreatmentNoteBody';
import { dailyNoteDraftUpdate, DailyNoteGoal, mapDailyTreatmentNote, DAILY_NOTE_NOT_STORED } from '../utils/dailyTreatmentNote';
import { mintedGuardianSend } from '../utils/mintedGuardianSend';
import {
  getCurrentPayPeriod,
  getAdjacentPayPeriod,
  getRecentPayPeriods,
  formatDisplayDate,
  AllegiantPayPeriod
} from '../utils/payPeriod';
import { NewClinicalDocModal } from './NewClinicalDocModal';
import { ClinicalDocStructuredDisplay } from './clinical/ClinicalDocStructuredDisplay';
import { BillingOperationalEvidencePanel } from './clinical/BillingOperationalEvidencePanel';
import { playPulseSeal } from '../utils/pulseAudio';
import { SupervisorAssistantSelector, AssistantProvider } from './clinical/SupervisorAssistantSelector';
import { MobilePeriodSelector, MobilePeriodOption } from './clinical/MobilePeriodSelector';

export type NotesTabMode = 'CURRENT_PERIOD' | 'NEEDS_APPROVAL' | 'ALL';

interface NotesViewProps {
  user: UserSession;
  onSelectClient: (clientId: string) => void;
  onNavigateTab?: (tab: string, subTab?: string) => void;
  initialTab?: NotesTabMode;
}

export const NotesView: React.FC<NotesViewProps> = ({
  user,
  onSelectClient,
  onNavigateTab,
  initialTab
}) => {
  const [docs, setDocs] = useState<any[]>([]);
  const [pendingSupervisionDocs, setPendingSupervisionDocs] = useState<any[]>([]);
  const docsRef = useRef<any[]>([]);
  const pendingRef = useRef<any[]>([]);
  const openingDocIdRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedDoc, setSelectedDoc] = useState<any | null>(null);
  const [showNewDocModal, setShowNewDocModal] = useState(false);

  // Canonical Allegiant Pay Period & Tab Mode State (defaults to NEEDS_APPROVAL for supervisor)
  const [selectedPeriod, setSelectedPeriod] = useState<AllegiantPayPeriod>(() => getCurrentPayPeriod());
  const [needsApprovalPeriodId, setNeedsApprovalPeriodId] = useState<string>('ALL_OPEN');
  const [selectedAssistantId, setSelectedAssistantId] = useState<string>('ALL');
  const [providersList, setProvidersList] = useState<any[]>([]);
  const [allPendingSupervisionDocs, setAllPendingSupervisionDocs] = useState<any[]>([]);

  const [tabMode, setTabMode] = useState<NotesTabMode>(() => {
    if (initialTab) return initialTab;
    if (user.role === 'SUPERVISOR') return 'NEEDS_APPROVAL';
    return 'CURRENT_PERIOD';
  });
  const [clearanceFilter, setClearanceFilter] = useState<'ALL' | 'CLEARED' | 'NEEDS_CORRECTION' | 'PENDING_GUARDIAN' | 'PENDING_SUPERVISOR'>('ALL');
  const recentPeriods = useMemo(() => getRecentPayPeriods(6, 4), []);

  const needsApprovalPeriodOptions: MobilePeriodOption[] = useMemo(() => [
    { id: 'ALL_OPEN', label: 'All Open Periods (All Unresolved)' },
    ...recentPeriods.map((p) => ({
      id: p.id,
      label: p.label,
      isCurrent: p.isCurrent,
    })),
  ], [recentPeriods]);

  const standardPeriodOptions: MobilePeriodOption[] = useMemo(() =>
    recentPeriods.map((p) => ({
      id: p.id,
      label: p.label,
      isCurrent: p.isCurrent,
    })),
  [recentPeriods]);

  useEffect(() => {
    if (initialTab && initialTab !== tabMode) {
      setTabMode(initialTab);
    }
  }, [initialTab]);

  useEffect(() => {
    if (user.role === 'SUPERVISOR' && !initialTab) {
      setTabMode('NEEDS_APPROVAL');
    }
  }, [user.role, initialTab]);

  // Inline editing state for clinician revisions
  const [isEditing, setIsEditing] = useState(false);
  const [editSubjective, setEditSubjective] = useState('');
  const [editObjective, setEditObjective] = useState('');
  const [editAssessment, setEditAssessment] = useState('');
  const [editPlan, setEditPlan] = useState('');
  const [dailyNarrative, setDailyNarrative] = useState('');
  const [dailyGoals, setDailyGoals] = useState<DailyNoteGoal[]>([]);
  const [dailyObservations, setDailyObservations] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Supervisor review action state
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnComments, setReturnComments] = useState('');
  const [processingSupervision, setProcessingSupervision] = useState(false);

  // Guardian eSign dispatch state
  const [sendingEsign, setSendingEsign] = useState(false);
  const [actionSuccessMessage, setActionSuccessMessage] = useState<string | null>(null);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [showIdentityDrawer, setShowIdentityDrawer] = useState(false);
  const [canonicalBillingIdentity, setCanonicalBillingIdentity] = useState<any>(null);

  // Desktop Header Container Width Observer (responds to rendered header width, e.g. laptop / split window)
  const [headerWidth, setHeaderWidth] = useState<number>(1200);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width > 0) setHeaderWidth(rect.width);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        if (width > 0) {
          setHeaderWidth(width);
        }
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const isCompactHeader = headerWidth < 980;

  // Mobile/PWA popstate back navigation support: returns to queue list without leaving page
  useEffect(() => {
    if (selectedDoc && typeof window !== 'undefined' && window.innerWidth <= 1024) {
      window.history.pushState({ noteDetailOpen: true }, '');
      const handlePopState = () => {
        setSelectedDoc(null);
      };
      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [selectedDoc]);

  useEffect(() => {
    fetch('/api/connections')
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.billingIdentity) {
          setCanonicalBillingIdentity(data.billingIdentity);
        }
      })
      .catch(err => console.warn('Failed to load canonical billing identity:', err));

    fetch('/api/providers')
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        setProvidersList(Array.isArray(data) ? data : []);
      })
      .catch(err => console.error('Failed to load providers:', err));
  }, []);

  const formatConciseDateTime = (dateStr?: string | null): string => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', ' +
             d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const getDocPayPeriod = (dosStr?: string): AllegiantPayPeriod | null => {
    if (!dosStr) return null;
    const targetDate = dosStr.slice(0, 10);
    for (const p of recentPeriods) {
      if (targetDate >= p.startDate && targetDate <= p.endDate) {
        return p;
      }
    }
    return null;
  };

  const isAssistant = user.role === 'ASSISTANT';
  const isSupervisor = user.role === 'SUPERVISOR' || user.role === 'OWNER' || user.role === 'ADMIN' || (user as any).activeRole === 'SUPERVISOR';
  const isClinicalStaff = user.role === 'SUPERVISOR' || user.role === 'ASSISTANT';
  const isSupervisorRole = isClinicalStaff;
  const isSupervisorMobile = isClinicalStaff;
  const isOwnerOrAdmin = user.role === 'OWNER' || user.role === 'ADMIN';

  // Canonical Supervisor → Assistant authorized list
  const authorizedAssistants = useMemo<AssistantProvider[]>(() => {
    // 1. Identify logged-in user's supervisor provider record
    let supervisorProv = providersList.find(p => 
      p.id === user.providerId || 
      p.user_id === user.id || 
      p.email === user.email
    );
    if (!supervisorProv && (user.role === 'SUPERVISOR' || (user as any).activeRole === 'SUPERVISOR')) {
      supervisorProv = providersList.find(p => p.role === 'SUPERVISOR' || p.role === 'SLP');
    }

    // 2. Canonical rule: assistants where supervisor_id === supervisorProv.id AND is an assistant/SLPA
    if (supervisorProv) {
      const supervised = providersList.filter(p => 
        p.supervisor_id === supervisorProv.id &&
        (p.role === 'SLPA' || p.role === 'ASSISTANT' || (p.title && p.title.includes('SLPA')) || Boolean(p.requires_supervision))
      );
      if (supervised.length > 0) return supervised;
    }

    // 3. For Owner/Admin acting as supervisor without a specific supervisor assignment, return all assistants
    if (isOwnerOrAdmin) {
      return providersList.filter(p => 
        p.role === 'SLPA' || p.role === 'ASSISTANT' || (p.title && p.title.includes('SLPA')) || Boolean(p.requires_supervision)
      );
    }

    return [];
  }, [providersList, user.providerId, user.id, user.email, user.role, (user as any).activeRole, isOwnerOrAdmin]);

  const selectedAssistant = useMemo(() => {
    if (selectedAssistantId === 'ALL') return null;
    return authorizedAssistants.find(a => a.id === selectedAssistantId) || null;
  }, [authorizedAssistants, selectedAssistantId]);

  const activeNeedsApprovalPeriod = useMemo(() => {
    if (needsApprovalPeriodId === 'ALL_OPEN') return null;
    return recentPeriods.find(p => p.id === needsApprovalPeriodId) || selectedPeriod;
  }, [needsApprovalPeriodId, recentPeriods, selectedPeriod]);

  const handleSelectPeriod = (period: AllegiantPayPeriod) => {
    setSelectedPeriod(period);
    if (tabMode === 'NEEDS_APPROVAL') {
      setNeedsApprovalPeriodId(period.id);
    } else {
      setTabMode('CURRENT_PERIOD');
    }
  };

  const handlePrevPeriod = () => {
    const base = tabMode === 'NEEDS_APPROVAL' && activeNeedsApprovalPeriod
      ? activeNeedsApprovalPeriod
      : selectedPeriod;
    handleSelectPeriod(getAdjacentPayPeriod(base, -1));
  };

  const handleNextPeriod = () => {
    const base = tabMode === 'NEEDS_APPROVAL' && activeNeedsApprovalPeriod
      ? activeNeedsApprovalPeriod
      : selectedPeriod;
    handleSelectPeriod(getAdjacentPayPeriod(base, 1));
  };

  const handleCurrentPeriod = () => {
    handleSelectPeriod(getCurrentPayPeriod());
  };

  const openDocument = async (id?: string | null, listHint?: any) => {
    if (!id) {
      setSelectedDoc(null);
      return;
    }
    openingDocIdRef.current = id;
    try {
      const res = await fetch(`/api/clinical/docs/${id}`);
      if (!res.ok) return;
      const full = await res.json();
      if (openingDocIdRef.current !== id) return;
      const listRow = listHint
        || docsRef.current.find((d: any) => d.id === id)
        || pendingRef.current.find((d: any) => d.id === id);
      setSelectedDoc(listRow ? { ...listRow, ...full } : full);
    } catch (err) {
      console.error('Error fetching treatment note detail:', err);
    }
  };

  const fetchPendingSupervisionDocs = async (): Promise<any[]> => {
    try {
      // 1. Fetch total backlog across all open periods and all assistants for accurate counters
      const totalRes = await fetch('/api/clinical/docs?pending_supervision=true');
      if (totalRes.ok) {
        const totalData = await totalRes.json();
        const allActionable = totalData.filter((d: any) => {
          if (d.doc_type !== 'SOAP' && d.doc_type !== 'QPR') return false;
          if (d.supervisor_approval_status === 'APPROVED' || d.supervisor_approval_status === 'RETURNED') return false;
          return d.supervisor_approval_status === 'PENDING' || d.is_supervisor_pending || !d.supervisor_approval_status;
        });
        setAllPendingSupervisionDocs(allActionable);
      }

      // 2. Fetch records narrowed by selected Assistant and/or Period
      const params = new URLSearchParams({ pending_supervision: 'true' });
      if (needsApprovalPeriodId !== 'ALL_OPEN') {
        const targetPeriod = recentPeriods.find(p => p.id === needsApprovalPeriodId) || selectedPeriod;
        params.set('startDate', targetPeriod.startDate);
        params.set('endDate', targetPeriod.endDate);
      }
      if (selectedAssistantId !== 'ALL') {
        params.set('provider_id', selectedAssistantId);
      }

      const res = await fetch(`/api/clinical/docs?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        const actionable = data.filter((d: any) => {
          if (d.doc_type !== 'SOAP' && d.doc_type !== 'QPR') return false;
          if (d.supervisor_approval_status === 'APPROVED' || d.supervisor_approval_status === 'RETURNED') return false;
          return d.supervisor_approval_status === 'PENDING' || d.is_supervisor_pending || !d.supervisor_approval_status;
        });
        setPendingSupervisionDocs(actionable);
        pendingRef.current = actionable;
        return actionable;
      }
    } catch (err) {
      console.error('Failed to load pending supervision docs:', err);
    }
    return [];
  };

  const fetchDocs = async (targetIdToSelect?: string) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ doc_type: 'SOAP' });
      if (tabMode === 'CURRENT_PERIOD') {
        params.set('startDate', selectedPeriod.startDate);
        params.set('endDate', selectedPeriod.endDate);
      }
      if (selectedAssistantId !== 'ALL') {
        params.set('provider_id', selectedAssistantId);
      }
      const res = await fetch(`/api/clinical/docs?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setDocs(data);
        docsRef.current = data;
        if (targetIdToSelect) {
          const match = data.find((d: any) => d.id === targetIdToSelect);
          if (match) await openDocument(match.id, match);
        } else if (tabMode !== 'NEEDS_APPROVAL') {
          const isDesktop = typeof window !== 'undefined' && window.innerWidth > 1024;
          if (selectedDoc) {
            const refreshed = data.find((d: any) => d.id === selectedDoc.id);
            if (refreshed) await openDocument(refreshed.id, refreshed);
            else if (isDesktop && data[0]) await openDocument(data[0].id, data[0]);
            else setSelectedDoc(null);
          } else if (data.length > 0 && isDesktop) {
            await openDocument(data[0].id, data[0]);
          } else {
            setSelectedDoc(null);
          }
        }
      }
    } catch (e) {
      console.error('Failed to load treatment notes:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
    fetchPendingSupervisionDocs();
  }, [tabMode, selectedPeriod.id, needsApprovalPeriodId, selectedAssistantId]);

  useEffect(() => {
    const isDesktop = typeof window !== 'undefined' && window.innerWidth > 1024;
    if (!isDesktop) return; // On mobile, preserve queue list until user explicitly selects a note

    if (tabMode === 'NEEDS_APPROVAL') {
      if (pendingSupervisionDocs.length > 0) {
        if (!selectedDoc || !pendingSupervisionDocs.find((d: any) => d.id === selectedDoc.id)) {
          void openDocument(pendingSupervisionDocs[0].id, pendingSupervisionDocs[0]);
        }
      } else {
        setSelectedDoc(null);
      }
    } else {
      if (docs.length > 0) {
        if (!selectedDoc || !docs.find((d: any) => d.id === selectedDoc.id)) {
          void openDocument(docs[0].id, docs[0]);
        }
      } else {
        setSelectedDoc(null);
      }
    }
  }, [tabMode, pendingSupervisionDocs.length]);

  useEffect(() => {
    if (selectedDoc && selectedDoc.doc_type === 'SOAP') {
      const note = mapDailyTreatmentNote(selectedDoc);
      setDailyNarrative(note.narrative === DAILY_NOTE_NOT_STORED ? '' : note.narrative);
      setDailyGoals(note.goals);
      setDailyObservations(note.observations.join('\n\n'));
      setEditSubjective(selectedDoc.subjective || '');
      setEditObjective(selectedDoc.objective || '');
      setEditAssessment(selectedDoc.assessment || '');
      setEditPlan(selectedDoc.plan || '');
      setIsEditing(false);
    }
  }, [selectedDoc?.id]);

  // Unique list of providers in dataset for filtering
  const availableProviders = useMemo(() => {
    const sourceList = tabMode === 'NEEDS_APPROVAL' ? pendingSupervisionDocs : docs;
    const map = new Map<string, string>();
    sourceList.forEach((d: any) => {
      if (d.provider_id && d.provider_name) {
        map.set(d.provider_id, `${d.provider_name} (${d.provider_role || 'Clinician'})`);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [tabMode, pendingSupervisionDocs, docs]);

  // Pre-cutoff clearance KPI counters
  const periodMetrics = useMemo(() => {
    let total = docs.length;
    let cleared = 0;
    let needsCorrection = 0;
    let pendingGuardian = 0;
    let pendingSupervisor = 0;

    docs.forEach((d: any) => {
      const isCleared = Boolean(d.is_cleared || d.clearance_status === 'CLEARED');
      const isReturned = d.clearance_status === 'NEEDS_CORRECTION' || d.supervisor_approval_status === 'RETURNED';
      if (isCleared) {
        cleared++;
      } else if (isReturned) {
        needsCorrection++;
      } else if (d.clearance_status === 'PENDING_GUARDIAN') {
        pendingGuardian++;
      } else if (d.clearance_status === 'PENDING_SUPERVISOR') {
        pendingSupervisor++;
      }
    });

    return { total, cleared, needsCorrection, pendingGuardian, pendingSupervisor };
  }, [docs]);

  // Filtered treatment notes or pending supervision items
  const filteredDocs = useMemo(() => {
    const source = tabMode === 'NEEDS_APPROVAL' ? pendingSupervisionDocs : docs;
    return source.filter((d: any) => {
      const matchesSearch = `${d.id || ''} ${d.client_id || ''} ${d.first_name || ''} ${d.last_name || ''} ${d.doc_type || ''} ${d.provider_name || ''} ${d.dos || ''} ${d.title || ''} ${d.qpr_quarter || ''}`
        .toLowerCase()
        .includes(search.toLowerCase());

      if (tabMode === 'NEEDS_APPROVAL') {
        return matchesSearch;
      }

      const isCleared = Boolean(d.is_cleared || d.clearance_status === 'CLEARED');
      const isReturned = d.clearance_status === 'NEEDS_CORRECTION' || d.supervisor_approval_status === 'RETURNED';
      const matchesClearance = clearanceFilter === 'ALL'
        ? true
        : clearanceFilter === 'CLEARED'
        ? isCleared
        : clearanceFilter === 'NEEDS_CORRECTION'
        ? isReturned
        : clearanceFilter === 'PENDING_GUARDIAN'
        ? (d.clearance_status === 'PENDING_GUARDIAN')
        : clearanceFilter === 'PENDING_SUPERVISOR'
        ? (d.clearance_status === 'PENDING_SUPERVISOR')
        : true;

      return matchesSearch && matchesClearance;
    }).sort((a: any, b: any) => {
      const dosA = a.dos || a.document_date || (a.created_at ? a.created_at.slice(0, 10) : '') || '';
      const dosB = b.dos || b.document_date || (b.created_at ? b.created_at.slice(0, 10) : '') || '';
      if (dosA !== dosB) {
        return dosB.localeCompare(dosA);
      }
      const createdA = a.created_at || '';
      const createdB = b.created_at || '';
      return createdB.localeCompare(createdA);
    });
  }, [tabMode, pendingSupervisionDocs, docs, search, clearanceFilter]);

  // Handle Save Revisions (clinician editing returned or pending note)
  const handleSaveRevisions = async () => {
    if (!selectedDoc) return;
    try {
      setSavingEdit(true);
      const res = await fetch(`/api/clinical/docs/${selectedDoc.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(selectedDoc.doc_type === 'SOAP'
          ? dailyNoteDraftUpdate(selectedDoc, {
            narrative: dailyNarrative,
            goals: dailyGoals,
            observations: dailyObservations
          })
          : {
            subjective: editSubjective,
            objective: editObjective,
            assessment: editAssessment,
            plan: editPlan
          })
      });

      if (res.ok) {
        setActionSuccessMessage('Note revisions saved successfully. Status resubmitted for supervisor review.');
        setIsEditing(false);
        await fetchDocs(selectedDoc.id);
        await fetchPendingSupervisionDocs();
        setTimeout(() => setActionSuccessMessage(null), 4000);
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to save note revisions.');
      }
    } catch (e) {
      console.error('Failed to save revisions:', e);
      alert('Network error saving revisions.');
    } finally {
      setSavingEdit(false);
    }
  };

  // Handle Supervisor Approval & Co-Sign
  const handleSupervisorApprove = async () => {
    if (!selectedDoc) return;
    try {
      setProcessingSupervision(true);
      const approvedId = selectedDoc.id;
      const res = await fetch(`/api/clinical/docs/${approvedId}/sign-supervisor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'APPROVE' })
      });

      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        playPulseSeal();
        const itemType = selectedDoc.doc_type === 'QPR' ? 'QPR' : 'Note';
        const payrollNote = data.payrollAssignmentMessage ? ` ${data.payrollAssignmentMessage}` : '';
        setActionSuccessMessage(`${itemType} co-signed and approved by supervisor.${payrollNote}`);

        // Ergonomic immediate queue updates:
        // 1. Calculate remaining records and index of approved item
        const currentIndex = pendingSupervisionDocs.findIndex((d: any) => d.id === approvedId);
        const remaining = pendingSupervisionDocs.filter((d: any) => d.id !== approvedId);

        // 2. Immediately update queue counts and pending docs array
        setPendingSupervisionDocs(remaining);
        pendingRef.current = remaining;

        // 3. Automatically select/open the next pending record
        if (tabMode === 'NEEDS_APPROVAL') {
          if (remaining.length > 0) {
            const nextDoc = remaining[currentIndex] || remaining[currentIndex - 1] || remaining[0];
            await openDocument(nextDoc.id, nextDoc);
          } else {
            setSelectedDoc(null);
          }
        }

        // Background sync to ensure full backend data consistency
        const updatedPending = await fetchPendingSupervisionDocs();
        await fetchDocs();
        if (tabMode === 'NEEDS_APPROVAL') {
          if (updatedPending.length > 0) {
            const keep = selectedDoc && updatedPending.find((d: any) => d.id === selectedDoc.id)
              ? selectedDoc
              : updatedPending[0];
            if (!selectedDoc || selectedDoc.id !== keep.id) {
              await openDocument(keep.id, keep);
            }
          } else {
            setSelectedDoc(null);
          }
        } else {
          await fetchDocs(approvedId);
        }
        setTimeout(() => setActionSuccessMessage(null), 4000);
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to approve document.');
      }
    } catch (e) {
      console.error('Supervisory approval failed:', e);
      alert('Network error approving document.');
    } finally {
      setProcessingSupervision(false);
    }
  };

  // Handle Supervisor Return for Revision
  const handleSupervisorReturn = async () => {
    if (!selectedDoc) return;
    if (!returnComments.trim()) {
      alert('Please provide specific revision instructions for the clinician.');
      return;
    }
    try {
      setProcessingSupervision(true);
      const returnedId = selectedDoc.id;
      const res = await fetch(`/api/clinical/docs/${returnedId}/sign-supervisor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'RETURN',
          comments: returnComments.trim()
        })
      });

      if (res.ok) {
        const itemType = selectedDoc.doc_type === 'QPR' ? 'QPR' : 'Note';
        setActionSuccessMessage(`${itemType} returned to clinician with revision instructions.`);
        setShowReturnModal(false);
        setReturnComments('');

        // Ergonomic immediate queue updates:
        const currentIndex = pendingSupervisionDocs.findIndex((d: any) => d.id === returnedId);
        const remaining = pendingSupervisionDocs.filter((d: any) => d.id !== returnedId);
        setPendingSupervisionDocs(remaining);
        pendingRef.current = remaining;

        if (tabMode === 'NEEDS_APPROVAL') {
          if (remaining.length > 0) {
            const nextDoc = remaining[currentIndex] || remaining[currentIndex - 1] || remaining[0];
            await openDocument(nextDoc.id, nextDoc);
          } else {
            setSelectedDoc(null);
          }
        }

        const updatedPending = await fetchPendingSupervisionDocs();
        await fetchDocs();
        if (tabMode === 'NEEDS_APPROVAL') {
          if (updatedPending.length > 0) {
            const keep = selectedDoc && updatedPending.find((d: any) => d.id === selectedDoc.id)
              ? selectedDoc
              : updatedPending[0];
            if (!selectedDoc || selectedDoc.id !== keep.id) {
              await openDocument(keep.id, keep);
            }
          } else {
            setSelectedDoc(null);
          }
        } else {
          await fetchDocs(returnedId);
        }
        setTimeout(() => setActionSuccessMessage(null), 4000);
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to return document.');
      }
    } catch (e) {
      console.error('Supervisory return failed:', e);
      alert('Network error returning document.');
    } finally {
      setProcessingSupervision(false);
    }
  };

  // Trigger SignWell Guardian Signature
  const handleSendGuardianEsign = async () => {
    if (!selectedDoc) return;
    if (selectedDoc.doc_type === 'SOAP' && !soapGuardianEsignAccess(selectedDoc).allowed) {
      alert('SignWell SOAP dispatch is unavailable. Routine treatment SOAPs require in-person signature in Mobile SOAP. Manual SOAP exception approval is required.');
      return;
    }
    try {
      setSendingEsign(true);

      const result = await mintedGuardianSend({
        clientId: selectedDoc.client_id,
        documentId: selectedDoc.id,
        recipientEmail: selectedDoc.guardian_email,
        sendUrl: `/api/clinical/docs/${selectedDoc.id}/send-signwell`,
        sendBody: (intentId) => ({ intentId })
      });
      if (result.stage === 'intent') {
        alert(result.error);
        return;
      }
      if (result.presentedAsSuccess) {
        setActionSuccessMessage('SignWell eSign envelope dispatched to parent/guardian.');
        await fetchDocs(selectedDoc.id);
        setTimeout(() => setActionSuccessMessage(null), 4000);
      } else {
        alert(result.data.error || result.data.failureReason || 'Failed to dispatch SignWell envelope.');
      }
    } catch (e) {
      console.error('SignWell dispatch error:', e);
      alert('Network error sending eSign request.');
    } finally {
      setSendingEsign(false);
    }
  };

  const isSelectedDocSlpa = selectedDoc && (
    selectedDoc.provider_role === 'SLPA' ||
    selectedDoc.provider_role === 'ASSISTANT' ||
    (selectedDoc.provider_title && selectedDoc.provider_title.includes('SLPA'))
  );

  const canSuperviseSelectedDoc = isSupervisor && isSelectedDocSlpa && (
    selectedDoc?.doc_type === 'SOAP' || selectedDoc?.doc_type === 'QPR'
  ) && (
    selectedDoc?.supervisor_approval_status !== 'APPROVED' &&
    selectedDoc?.supervisor_approval_status !== 'RETURNED'
  );

  const sectionData = useMemo(() => {
    if (!selectedDoc?.section_data_json) {
      return selectedDoc?.section_data || {};
    }
    try {
      return typeof selectedDoc.section_data_json === 'string'
        ? JSON.parse(selectedDoc.section_data_json)
        : selectedDoc.section_data_json;
    } catch {
      return selectedDoc?.section_data || {};
    }
  }, [selectedDoc]);

  // Desktop Period Selector JSX helper (used identically in Wide and Compact modes)
  const renderDesktopPeriodSelector = () => {
    if (tabMode === 'NEEDS_APPROVAL') {
      return (
        <div className="w-full sm:w-[260px] sm:shrink-0 h-8 px-2 rounded-lg border flex items-center justify-between text-xs font-mono font-medium border-slate-300 bg-white hover:border-slate-400/70 text-slate-900 shadow-xs">
          <button
            type="button"
            onClick={handlePrevPeriod}
            title="Previous Pay Period"
            className="p-1 rounded hover:bg-slate-100 text-slate-600 hover:text-slate-900 transition-colors shrink-0 cursor-pointer"
          >
            <ChevronLeft className="w-3.5 h-3.5 text-slate-600" />
          </button>

          <div className="flex items-center space-x-1.5 min-w-0 flex-1 justify-center px-1 overflow-hidden">
            <Calendar className="w-3.5 h-3.5 shrink-0 text-slate-600" />
            <select
              value={needsApprovalPeriodId}
              onChange={(e) => setNeedsApprovalPeriodId(e.target.value)}
              className="bg-transparent border-0 text-slate-900 font-mono text-[11px] font-semibold focus:outline-none cursor-pointer truncate max-w-full text-center"
            >
              <option value="ALL_OPEN" className="bg-white text-slate-900 font-semibold">
                All Open Periods
              </option>
              {recentPeriods.map((p) => (
                <option key={p.id} value={p.id} className="bg-white text-slate-900">
                  {p.label} {p.isCurrent ? '★ Current' : ''}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={handleNextPeriod}
            title="Next Pay Period"
            className="p-1 rounded hover:bg-slate-100 text-slate-600 hover:text-slate-900 transition-colors shrink-0 cursor-pointer"
          >
            <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
          </button>
        </div>
      );
    }

    return (
      <div className="w-full sm:w-[260px] sm:shrink-0 h-8 px-2 rounded-lg border flex items-center justify-between text-xs font-mono font-medium border-slate-300 bg-white hover:border-slate-400/70 text-slate-900 shadow-xs">
        <button
          type="button"
          onClick={handlePrevPeriod}
          title="Previous Pay Period"
          className="p-1 rounded hover:bg-slate-100 text-slate-600 hover:text-slate-900 transition-colors shrink-0 cursor-pointer"
        >
          <ChevronLeft className="w-3.5 h-3.5 text-slate-600" />
        </button>

        <div className="flex items-center space-x-1.5 min-w-0 flex-1 justify-center px-1 overflow-hidden">
          <Calendar className="w-3.5 h-3.5 text-slate-600 shrink-0" />
          <select
            value={selectedPeriod.id}
            onChange={(e) => {
              const target = recentPeriods.find(p => p.id === e.target.value);
              if (target) handleSelectPeriod(target);
            }}
            className="bg-transparent border-0 text-slate-900 font-mono text-[11px] font-semibold focus:outline-none cursor-pointer truncate max-w-full text-center"
          >
            {recentPeriods.map((p) => (
              <option key={p.id} value={p.id} className="bg-white text-slate-900">
                {p.label} {p.isCurrent ? '★ Current Period' : ''}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={handleNextPeriod}
          title="Next Pay Period"
          className="p-1 rounded hover:bg-slate-100 text-slate-600 hover:text-slate-900 transition-colors shrink-0 cursor-pointer"
        >
          <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
        </button>
      </div>
    );
  };

  // Approval Tabs Segmented Control JSX helper
  const renderApprovalTabs = () => (
    <div className="flex items-center gap-1 border shadow-xs p-0.5 sm:p-1 rounded-xl text-xs w-full sm:w-auto shrink-0 bg-slate-100/90 border-slate-200">
      <button
        onClick={() => setTabMode('NEEDS_APPROVAL')}
        className={`flex-1 sm:flex-none px-2 sm:px-2.5 py-1 rounded font-semibold text-[11px] transition-all flex items-center justify-center space-x-1 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
          tabMode === 'NEEDS_APPROVAL'
            ? 'bg-white text-slate-900 border border-slate-300 shadow-xs'
            : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
        }`}
        id="notes-tab-needs-approval"
      >
        <span>Needs Approval</span>
        {pendingSupervisionDocs.length > 0 && (
          <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
            tabMode === 'NEEDS_APPROVAL'
              ? 'bg-amber-100 text-amber-900 border border-amber-300'
              : 'bg-amber-500 text-slate-950'
          }`}>
            {pendingSupervisionDocs.length}
          </span>
        )}
      </button>
      <button
        onClick={() => setTabMode('CURRENT_PERIOD')}
        className={`flex-1 sm:flex-none px-2 sm:px-2.5 py-1 rounded font-semibold text-[11px] transition-colors whitespace-nowrap text-center cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
          tabMode === 'CURRENT_PERIOD'
            ? 'bg-white text-slate-900 border border-slate-300 shadow-xs'
            : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
        }`}
        id="notes-tab-period"
      >
        Period Notes
      </button>
      <button
        onClick={() => setTabMode('ALL')}
        className={`flex-1 sm:flex-none px-2 sm:px-2.5 py-1 rounded font-semibold text-[11px] transition-colors whitespace-nowrap text-center cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
          tabMode === 'ALL'
            ? 'bg-white text-slate-900 border border-slate-300 shadow-xs'
            : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
        }`}
        id="notes-tab-all"
      >
        All Records
      </button>
    </div>
  );

  // Status Metrics / Clearance Filter Buttons JSX helper
  const renderStatusMetrics = () => {
    if (tabMode === 'NEEDS_APPROVAL') {
      return (
        <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-mono shrink-0">
          <span className="px-2 py-0.5 rounded font-semibold shadow-xs bg-amber-50 border border-amber-200 text-amber-900">
            Awaiting: <strong className="text-amber-950 ml-0.5">{pendingSupervisionDocs.length}</strong>
          </span>
          <span className="px-1.5 py-0.5 rounded border bg-sky-50 border-sky-200 text-sky-900">
            SOAP: <strong className="text-sky-950">{pendingSupervisionDocs.filter(d => d.doc_type === 'SOAP').length}</strong>
          </span>
          <span className="px-1.5 py-0.5 rounded border bg-amber-50 border-amber-200 text-amber-900">
            QPR: <strong className="text-amber-950">{pendingSupervisionDocs.filter(d => d.doc_type === 'QPR').length}</strong>
          </span>
        </div>
      );
    }

    return (
      <div className="flex flex-wrap items-center gap-1 text-[10.5px]">
        <button
          onClick={() => setClearanceFilter('ALL')}
          className={`px-2 py-0.5 rounded font-mono transition-all cursor-pointer ${
            clearanceFilter === 'ALL'
              ? 'bg-slate-100 text-slate-900 border border-slate-300 font-semibold shadow-xs'
              : 'bg-white text-slate-600 border border-slate-200 hover:text-slate-900'
          }`}
        >
          All ({periodMetrics.total})
        </button>
        <button
          onClick={() => setClearanceFilter('CLEARED')}
          className={`px-2 py-0.5 rounded font-mono transition-all cursor-pointer ${
            clearanceFilter === 'CLEARED'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-300 font-semibold shadow-xs'
              : 'bg-white text-emerald-700 border border-slate-200 hover:bg-emerald-50/50'
          }`}
        >
          Cleared ({periodMetrics.cleared})
        </button>
        <button
          onClick={() => setClearanceFilter('PENDING_GUARDIAN')}
          className={`px-2 py-0.5 rounded font-mono transition-all cursor-pointer ${
            clearanceFilter === 'PENDING_GUARDIAN'
              ? 'bg-amber-50 text-amber-800 border border-amber-300 font-semibold shadow-xs'
              : 'bg-white text-amber-700 border border-slate-200 hover:bg-amber-50/50'
          }`}
        >
          Guardian ({periodMetrics.pendingGuardian})
        </button>
        <button
          onClick={() => setClearanceFilter('PENDING_SUPERVISOR')}
          className={`px-2 py-0.5 rounded font-mono transition-all cursor-pointer ${
            clearanceFilter === 'PENDING_SUPERVISOR'
              ? 'bg-amber-50 text-amber-900 border border-amber-300 font-semibold shadow-xs'
              : 'bg-white text-amber-800 border border-slate-200 hover:bg-amber-50/50'
          }`}
        >
          Co-Sign ({periodMetrics.pendingSupervisor})
        </button>
        <button
          onClick={() => setClearanceFilter('NEEDS_CORRECTION')}
          className={`px-2 py-0.5 rounded font-mono transition-all cursor-pointer ${
            clearanceFilter === 'NEEDS_CORRECTION'
              ? 'bg-rose-50 text-rose-800 border border-rose-300 font-semibold shadow-xs'
              : 'bg-white text-rose-700 border border-slate-200 hover:bg-rose-50/50'
          }`}
        >
          Revision ({periodMetrics.needsCorrection})
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-3.5 sm:space-y-4">
      {/* Condensed Operational Queue Header & Toolbar */}
      <div
        ref={headerRef}
        className="relative overflow-hidden rounded-2xl border px-3.5 py-3 sm:px-5 sm:py-3.5 space-y-2.5 sm:space-y-3 rounded-2xl border-slate-700/80 desktop-header-shell shadow-md"
      >
        <div className="hidden sm:block absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/30 to-transparent pointer-events-none" />
        <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />

        {/* Header Row 1: Centered "Note Approval" at the top of the page header */}
        <div className="relative flex items-center justify-center min-h-[32px] w-full">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight leading-none text-center text-white">
            Note Approval
          </h1>

          <div className="absolute right-0 inset-y-0 flex items-center gap-1.5 sm:gap-2 text-xs shrink-0">
            {((tabMode === 'NEEDS_APPROVAL' && activeNeedsApprovalPeriod) || (tabMode !== 'NEEDS_APPROVAL')) && (
              <div className="hidden xl:flex items-center space-x-1.5 text-[10.5px] font-mono text-slate-300">
                <span className="px-2 py-0.5 rounded bg-slate-900 border border-slate-800 text-amber-300">
                  Cutoff: {formatDisplayDate((tabMode === 'NEEDS_APPROVAL' ? activeNeedsApprovalPeriod! : selectedPeriod).cutoffDate)}
                </span>
                <span className="px-2 py-0.5 rounded bg-slate-900 border border-slate-800 text-emerald-300">
                  Pay: {formatDisplayDate((tabMode === 'NEEDS_APPROVAL' ? activeNeedsApprovalPeriod! : selectedPeriod).payDate)}
                </span>
              </div>
            )}

            {/* Mobile-only Refresh in Row 1; moved to Row 3 on desktop */}
            <button
              onClick={() => {
                fetchPendingSupervisionDocs();
                fetchDocs();
              }}
              className="sm:hidden px-2 py-1 rounded-lg border text-[11px] font-mono flex items-center space-x-1.5 transition-all cursor-pointer shadow-xs active:scale-95 shrink-0 bg-slate-800/80 border-slate-600 text-slate-200 hover:bg-slate-700 hover:text-white"
              title="Refresh Queue"
            >
              <RefreshCw className="w-3 h-3 text-slate-300" />
              <span className="hidden xs:inline">Refresh</span>
            </button>

            {user.role === 'ADMIN' && (
              <button
                onClick={() => setShowNewDocModal(true)}
                className="px-2 sm:px-2.5 py-1 rounded-lg bg-gradient-to-b from-[#1e3a5f] to-[#142640] hover:from-[#254877] hover:to-[#182f50] text-white font-bold text-[11px] flex items-center space-x-1 border border-sky-400/50 shadow-sm transition-all shrink-0"
              >
                <PlusCircle className="w-3.5 h-3.5" />
                <span>+ New</span>
              </button>
            )}
          </div>
        </div>

        {/* Header Row 2: Assistant and Period selectors together in a centered second row */}
        {/* Desktop: Centered together with equal pill widths and heights */}
        <div className="hidden sm:flex flex-wrap items-center justify-center gap-2.5 w-full">
          <div className="shrink-0">
            <SupervisorAssistantSelector
              assistants={authorizedAssistants}
              selectedAssistantId={selectedAssistantId}
              onSelectAssistant={setSelectedAssistantId}
              isOwnerOrAdmin={isOwnerOrAdmin}
              hideLabel={true}
              className="w-full sm:w-[260px] sm:shrink-0"
              lightTheme={true}
            />
          </div>
          <div className="shrink-0">
            {renderDesktopPeriodSelector()}
          </div>
        </div>

        {/* Mobile: Centered selectors */}
        <div className="sm:hidden flex flex-col items-center gap-2 text-xs w-full">
          <div className="w-full max-w-sm px-[38px] flex justify-center">
            <SupervisorAssistantSelector
              assistants={authorizedAssistants}
              selectedAssistantId={selectedAssistantId}
              onSelectAssistant={setSelectedAssistantId}
              isOwnerOrAdmin={isOwnerOrAdmin}
              hideLabel={true}
              hideTriggerCount={true}
              lightTheme={true}
            />
          </div>
          <div className="w-full max-w-sm flex justify-center">
            {tabMode === 'NEEDS_APPROVAL' ? (
              <MobilePeriodSelector
                options={needsApprovalPeriodOptions}
                selectedId={needsApprovalPeriodId}
                onSelect={(id) => setNeedsApprovalPeriodId(id)}
                onPrev={handlePrevPeriod}
                onNext={handleNextPeriod}
                iconColor="text-slate-600"
                accentColor="slate"
                lightTheme={true}
              />
            ) : (
              <MobilePeriodSelector
                options={standardPeriodOptions}
                selectedId={selectedPeriod.id}
                onSelect={(id) => {
                  const target = recentPeriods.find(p => p.id === id);
                  if (target) handleSelectPeriod(target);
                }}
                onPrev={handlePrevPeriod}
                onNext={handleNextPeriod}
                iconColor="text-emerald-400"
                accentColor="emerald"
                lightTheme={true}
              />
            )}
          </div>
        </div>

        {/* Header Row 3 (Desktop): Centered together in order:
            Needs Approval → Period Notes → All Records → Awaiting count → SOAP count → QPR count → Refresh */}
        <div className="hidden sm:flex flex-wrap items-center justify-center gap-2 w-full">
          {/* Approval Tabs */}
          <div className="flex items-center gap-1 border border-slate-200/90 shadow-none p-0.5 sm:p-1 rounded-xl text-xs shrink-0 bg-slate-100">
            <button
              onClick={() => setTabMode('NEEDS_APPROVAL')}
              className={`px-2.5 py-1 rounded-lg font-semibold text-[11px] transition-all flex items-center justify-center space-x-1 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
                tabMode === 'NEEDS_APPROVAL'
                  ? 'bg-slate-800 text-white border border-slate-600 shadow-xs'
                  : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
              }`}
              id="notes-tab-needs-approval"
            >
              <span>Needs Approval</span>
            </button>
            <button
              onClick={() => setTabMode('CURRENT_PERIOD')}
              className={`px-2.5 py-1 rounded-lg font-semibold text-[11px] transition-colors whitespace-nowrap text-center cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
                tabMode === 'CURRENT_PERIOD'
                  ? 'bg-slate-800 text-white border border-slate-600 shadow-xs'
                  : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
              }`}
              id="notes-tab-period"
            >
              Period Notes
            </button>
            <button
              onClick={() => setTabMode('ALL')}
              className={`px-2.5 py-1 rounded-lg font-semibold text-[11px] transition-colors whitespace-nowrap text-center cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
                tabMode === 'ALL'
                  ? 'bg-slate-800 text-white border border-slate-600 shadow-xs'
                  : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
              }`}
              id="notes-tab-all"
            >
              All Records
            </button>
          </div>

          {/* Counts */}
          <div className="flex items-center flex-wrap gap-1.5">
            {tabMode === 'NEEDS_APPROVAL' ? (
              <>
                <span className="h-7 px-2.5 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-300 font-semibold font-mono text-[11px] flex items-center shrink-0">
                  Awaiting: <strong className="text-white ml-1">{pendingSupervisionDocs.length}</strong>
                </span>
                <span className="h-7 px-2 rounded-lg border font-mono text-[11px] flex items-center shrink-0 bg-[#141c2a] border-slate-700/60 text-slate-300">
                  SOAP: <strong className="text-sky-300 ml-1">{pendingSupervisionDocs.filter(d => d.doc_type === 'SOAP').length}</strong>
                </span>
                <span className="h-7 px-2 rounded-lg border font-mono text-[11px] flex items-center shrink-0 bg-[#141c2a] border-slate-700/60 text-slate-300">
                  QPR: <strong className="text-amber-300 ml-1">{pendingSupervisionDocs.filter(d => d.doc_type === 'QPR').length}</strong>
                </span>
              </>
            ) : (
              renderStatusMetrics()
            )}
          </div>

          {/* Refresh button */}
          <button
            onClick={() => {
              fetchPendingSupervisionDocs();
              fetchDocs();
            }}
            className="h-7 px-2.5 rounded-lg border text-[11px] font-mono flex items-center space-x-1.5 transition-colors cursor-pointer active:scale-98 shrink-0 bg-[#141c2a] border-slate-700/60 hover:bg-[#1a2436] hover:border-slate-600 text-slate-300 hover:text-white"
            title="Refresh Queue"
          >
            <RefreshCw className="w-3 h-3 text-slate-300" />
            <span>Refresh</span>
          </button>
        </div>

        {/* Mobile Header Row 3: Approval tabs */}
        <div className="sm:hidden flex items-center justify-center w-full">
          {renderApprovalTabs()}
        </div>

        {/* Mobile Header Row 4: Counts and search */}
        <div className="sm:hidden border-t border-slate-800/80 pt-2 flex flex-col gap-2 w-full">
          <div className="flex items-center justify-center flex-wrap gap-1.5 min-w-0">
            {renderStatusMetrics()}
          </div>
          <div className="w-full">
            <ArcSearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search records..."
              className="w-full text-xs"
            />
          </div>
        </div>
      </div>

      {actionSuccessMessage && (
        <div className="p-2.5 rounded-lg bg-emerald-950/60 border border-emerald-500/50 text-emerald-300 text-xs flex items-center space-x-2 animate-fadeIn">
          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
          <span>{actionSuccessMessage}</span>
        </div>
      )}

      {/* Main Workspace Layout: Side-by-side on XL (>=1280px), stacked on smaller desktop and mobile */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-3.5 sm:gap-4">
        {/* Left Column: Note List - Hidden on mobile phone when note is open, stacked on smaller desktop */}
        <div className={`${selectedDoc ? 'hidden sm:block' : 'block'} xl:col-span-5 space-y-2`}>
            {/* Queue Column Mini-Header */}
          <div className="px-2.5 py-1.5 flex items-center justify-between gap-2 text-[10px] font-mono font-bold uppercase tracking-wider rounded-t-lg text-slate-200 bg-[#162030] border border-slate-700/60 border-b-0">
            <span className="shrink-0">CLIENT / DOCUMENT</span>

            {/* Desktop centered search records input */}
            <div className="hidden sm:flex flex-1 max-w-[200px] md:max-w-[240px] mx-auto normal-case font-sans">
              <ArcSearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search records..."
                className="w-full text-[11px] py-0.5 px-2 h-[26px] shadow-none !bg-[#0f1522] !border-slate-700/70 focus-within:!border-slate-400 focus-within:!ring-0"
                inputClassName="text-[11px] py-0"
              />
            </div>

            <span className="shrink-0 text-right">DOS • STATUS</span>
          </div>

          {/* Notes Scroll List */}
          <div className="space-y-1 max-h-72 xl:max-h-[calc(100vh-17rem)] overflow-y-auto pr-1">
            {loading ? (
              <div className="p-8 text-center text-slate-500 text-xs">
                <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-sky-500/70" />
                {tabMode === 'NEEDS_APPROVAL' ? 'Loading pending supervisor review documents...' : `Loading treatment notes for ${selectedPeriod.label}...`}
              </div>
            ) : filteredDocs.length === 0 ? (
              <div className="p-6 text-center rounded-xl border text-xs space-y-3 bg-white border-slate-200/90 text-slate-600 shadow-xs">
                <div className="text-slate-900 font-semibold">
                  {tabMode === 'NEEDS_APPROVAL' ? (
                    selectedAssistant && needsApprovalPeriodId !== 'ALL_OPEN'
                      ? `No notes requiring approval for ${selectedAssistant.name} during ${activeNeedsApprovalPeriod?.label || 'this period'}.`
                      : selectedAssistant
                      ? `No notes requiring approval for ${selectedAssistant.name} across all open pay periods.`
                      : needsApprovalPeriodId !== 'ALL_OPEN'
                      ? `No notes requiring approval across all assistants during ${activeNeedsApprovalPeriod?.label || 'this period'}.`
                      : 'No documents currently awaiting your supervisory approval. All SLPA notes and QPRs are cleared!'
                  ) : tabMode === 'CURRENT_PERIOD' ? (
                    selectedAssistant
                      ? `No period notes for ${selectedAssistant.name} during ${selectedPeriod.label}.`
                      : `No treatment notes match the current filters for ${selectedPeriod.label}.`
                  ) : (
                    selectedAssistant
                      ? `No clinical records found for ${selectedAssistant.name}.`
                      : 'No clinical records match the current filters.'
                  )}
                </div>

                {/* Reset / Broaden action buttons */}
                <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                  {selectedAssistantId !== 'ALL' && (
                    <button
                      onClick={() => setSelectedAssistantId('ALL')}
                      className="px-2.5 py-1 rounded-lg font-semibold text-[11px] transition-colors cursor-pointer bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300"
                    >
                      View All My Assistants
                    </button>
                  )}
                  {tabMode === 'NEEDS_APPROVAL' && needsApprovalPeriodId !== 'ALL_OPEN' && (
                    <button
                      onClick={() => setNeedsApprovalPeriodId('ALL_OPEN')}
                      className="px-2.5 py-1 rounded-lg font-semibold text-[11px] transition-colors cursor-pointer bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300"
                    >
                      View All Open Periods
                    </button>
                  )}
                  {tabMode === 'CURRENT_PERIOD' && !selectedPeriod.isCurrent && (
                    <button
                      onClick={handleCurrentPeriod}
                      className="px-2.5 py-1 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30 font-semibold text-[11px] transition-colors cursor-pointer"
                    >
                      Go to Current Pay Period
                    </button>
                  )}
                  {search && (
                    <button
                      onClick={() => setSearch('')}
                      className="px-2.5 py-1 rounded-lg font-semibold text-[11px] transition-colors cursor-pointer bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300"
                    >
                      Clear Search
                    </button>
                  )}
                </div>
              </div>
            ) : (
              filteredDocs.map((doc: any, idx: number) => {
                const isSelected = selectedDoc?.id === doc.id;
                const isSlpa = doc.provider_role === 'SLPA' || doc.provider_role === 'ASSISTANT' || (doc.provider_title && doc.provider_title.includes('SLPA'));
                const isCleared = doc.is_cleared || doc.clearance_status === 'CLEARED';
                const isReturned = doc.clearance_status === 'NEEDS_CORRECTION' || doc.supervisor_approval_status === 'RETURNED';
                const isPendingGuardian = doc.clearance_status === 'PENDING_GUARDIAN';
                const isPendingSupervisor = isSlpa && doc.clearance_status === 'PENDING_SUPERVISOR';

                const actualDuration = doc.actual_duration_minutes != null
                  ? Number(doc.actual_duration_minutes)
                  : doc.duration_minutes != null
                  ? Number(doc.duration_minutes)
                  : doc.planned_duration_minutes != null
                  ? Number(doc.planned_duration_minutes)
                  : null;

                return (
                  <div
                    key={doc.id}
                    onClick={() => { void openDocument(doc.id, doc); }}
                    className={`group relative overflow-hidden px-3 py-2 sm:px-3.5 sm:py-2.5 rounded-md border transition-colors duration-150 cursor-pointer select-none ${
                      isSelected
                        ? 'bg-slate-100 border-slate-300 border-l-[3.5px] border-l-slate-800 shadow-xs'
                        : `${idx % 2 === 0 ? 'bg-white' : 'bg-[#f8fafc]'} border-slate-200/90 hover:bg-slate-50 hover:border-slate-300 shadow-[0_1px_2px_rgba(15,23,42,0.03)]`
                    }`}
                  >
                    {/* Row 1: Status Pip + Client Name + Doc Badge + Status Badge */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center space-x-2 min-w-0">
                        {/* Status pip */}
                        <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                          isSelected
                            ? 'bg-slate-800'
                            : isReturned
                            ? 'bg-rose-500'
                            : isCleared
                            ? 'bg-emerald-500'
                            : isPendingSupervisor
                            ? 'bg-amber-500'
                            : isPendingGuardian
                            ? 'bg-amber-500'
                            : 'bg-slate-400'
                        }`} />

                        <span className={`font-semibold text-xs truncate ${
                          isSelected
                            ? 'text-slate-950 font-bold'
                            : 'text-slate-900 group-hover:text-slate-950'
                        }`}>
                          {doc.first_name} {doc.last_name}
                        </span>

                        <span className={`px-1.5 py-0.2 rounded text-[9px] font-mono font-bold shrink-0 ${
                          doc.doc_type === 'QPR'
                            ? 'bg-amber-50 text-amber-800 border border-amber-200'
                            : 'bg-sky-50 text-sky-800 border border-sky-200'
                        }`}>
                          {doc.doc_type || 'SOAP'}
                        </span>
                      </div>

                      {/* Operational Status Pill */}
                      <span className={`text-[9px] font-mono px-1.5 py-0.2 rounded font-bold shrink-0 uppercase border ${
                        isCleared
                          ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                          : isReturned
                          ? 'bg-rose-50 text-rose-800 border border-rose-200'
                          : isPendingSupervisor
                          ? 'bg-amber-50 text-amber-900 border border-amber-200'
                          : isPendingGuardian
                          ? 'bg-amber-50 text-amber-800 border border-amber-200'
                          : 'bg-slate-100 text-slate-700 border border-slate-200'
                      }`}>
                        {isCleared
                          ? 'CLEARED'
                          : isReturned
                          ? 'REVISION'
                          : isPendingSupervisor
                          ? 'CO-SIGN'
                          : isPendingGuardian
                          ? 'GUARDIAN'
                          : 'PENDING'}
                      </span>
                    </div>

                    {/* Row 2: DOS / Quarter • Provider • CPT/Duration */}
                    <div className="mt-1 flex items-center justify-between gap-2 text-[10.5px] font-mono">
                      <div className="flex items-center space-x-1.5 truncate text-slate-600">
                        <span className="font-semibold shrink-0 text-slate-800">
                          {doc.doc_type === 'QPR'
                            ? (doc.qpr_quarter || 'Q1 2026')
                            : (doc.dos || doc.document_date || 'N/A')}
                        </span>
                        <span className="text-slate-400">•</span>
                        <span className="truncate">
                          {doc.provider_name || 'Assigned Clinician'}
                          {isSlpa && <span className="text-sky-700 ml-1 font-semibold">(SLPA)</span>}
                        </span>
                      </div>

                      <div className="text-[10px] shrink-0 text-slate-600">
                        {doc.doc_type === 'QPR' ? (
                          <span className="text-amber-800 font-semibold">QPR</span>
                        ) : actualDuration != null ? (
                          <span>{doc.service_code || 'CPT'} • {actualDuration}m</span>
                        ) : (
                          <span>{doc.service_code || 'SOAP'}</span>
                        )}
                      </div>
                    </div>

                    {/* Actionable supervisor return comment preview if applicable */}
                    {isReturned && doc.supervisor_return_comments && (
                      <div className="mt-1 px-2 py-0.5 rounded text-[10px] truncate bg-rose-50 border border-rose-200 text-rose-800">
                        <strong className="text-rose-900">Return: </strong>
                        {doc.supervisor_return_comments}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Column: Note Detail, Supervisor Actions, & Clinician Revision Workspace */}
        <div className={`${selectedDoc ? 'block' : 'hidden xl:block'} xl:col-span-7 space-y-3`}>
          {selectedDoc ? (
            <>
              {/* Mobile Back Button: List → Detail → Back */}
              <div className="sm:hidden no-print">
                <button
                  type="button"
                  onClick={() => setSelectedDoc(null)}
                  className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl font-medium text-xs shadow-xs transition-all active:scale-[0.99] cursor-pointer bg-white border border-slate-200/90 text-slate-800 hover:bg-slate-50"
                >
                  <div className="flex items-center space-x-2">
                    <ArrowLeft className="w-4 h-4 text-slate-600" />
                    <span>Back to Approvals Queue</span>
                  </div>
                  <span className="text-[10px] font-mono text-slate-500">Preserved Queue State</span>
                </button>
              </div>

              {/* Unified Document Detail Card */}
              <div className="relative rounded-xl border overflow-hidden space-y-3 pb-4 bg-white border-slate-200/90 shadow-[0_1px_4px_rgba(15,23,42,0.06)]">
                {/* Unified Note Header */}
                <div className="border-b border-slate-200/90 flex flex-col w-full">
                  {(() => {
                    const docPeriod = getDocPayPeriod(selectedDoc.dos || selectedDoc.document_date);
                    const isSelectedDocSlpa = selectedDoc.provider_role === 'SLPA' || selectedDoc.provider_role === 'ASSISTANT' || (selectedDoc.provider_title && selectedDoc.provider_title.includes('SLPA'));
                    const isApproved = selectedDoc.supervisor_approval_status === 'APPROVED';
                    const isReturned = selectedDoc.supervisor_approval_status === 'RETURNED' || selectedDoc.clearance_status === 'NEEDS_CORRECTION';

                    return (
                      <>
                        {/* Desktop Note Header Top Row: Canonical Slate-Blue / Navy Structural Header */}
                        <div className="hidden sm:block px-4 py-3 sm:px-5 sm:py-3.5 bg-gradient-to-r from-[#2a394d] via-[#1a2a42] to-[#0d1727] border-b border-slate-700/80 w-full shrink-0">
                          {/* Row 1: Client Identity + Type & Status Badges */}
                          <div className="flex items-center space-x-2.5 flex-wrap gap-y-1 w-full">
                            <h2 className="text-base sm:text-lg font-bold tracking-tight text-white">
                              {selectedDoc.first_name} {selectedDoc.last_name}
                            </h2>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold shrink-0 ${
                              selectedDoc.doc_type === 'QPR'
                                ? 'bg-amber-500/20 text-amber-300 border border-amber-400/40'
                                : 'bg-sky-500/20 text-sky-300 border border-sky-400/40'
                            }`}>
                              {selectedDoc.doc_type || 'SOAP'}
                            </span>

                            {/* Prominent Approval Status Badge */}
                            <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold shrink-0 ${
                              isApproved
                                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/40'
                                : isReturned
                                ? 'bg-rose-500/20 text-rose-300 border border-rose-400/40'
                                : 'bg-amber-500/20 text-amber-300 border border-amber-400/40'
                            }`}>
                              {isApproved ? 'APPROVED' : isReturned ? 'RETURNED' : 'AWAITING APPROVAL'}
                            </span>

                            {selectedDoc.doc_type === 'SOAP' && selectedDoc.is_manual_soap && (
                              <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded shrink-0 bg-amber-500/20 text-amber-300 border border-amber-400/40">
                                Manual Exception: {selectedDoc.manual_soap_reason || 'Approved'}
                              </span>
                            )}
                          </div>

                          {/* Row 2: Supporting Clinical Context (uses full available width) */}
                          <div className="flex items-center space-x-2 text-[11px] font-mono flex-wrap gap-y-0.5 w-full text-slate-300/90 mt-1">
                            <span>
                              Treating Assistant: <strong className="text-white font-bold">{selectedDoc.provider_name}</strong>
                              {isSelectedDocSlpa && (
                                <span className="text-[10px] px-1.5 py-0.2 rounded font-semibold ml-1 bg-sky-500/20 border border-sky-400/40 text-sky-300">
                                  SLPA
                                </span>
                              )}
                            </span>
                            <span className="text-slate-500">•</span>
                            {selectedDoc.doc_type === 'QPR' ? (
                              <span>Reporting Quarter: <strong className="text-slate-100">{selectedDoc.qpr_quarter || 'Quarterly'}</strong></span>
                            ) : (
                              <span>DOS: <strong className="text-slate-100">{selectedDoc.dos || selectedDoc.document_date}</strong></span>
                            )}
                            {docPeriod && (
                              <>
                                <span className="text-slate-500">•</span>
                                <span>Pay Period: <strong className="text-slate-100">{docPeriod.label}</strong></span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Mobile Note Card Header (sm:hidden): Preserved Light Styling */}
                        <div className="sm:hidden px-3.5 py-2.5 border-b border-slate-200/90 bg-[#f8fafc] flex flex-col items-center justify-center text-center space-y-1.5 pb-0.5 w-full">
                          <div className="flex items-center space-x-2">
                            <h2 className="text-base font-bold tracking-tight text-slate-900">
                              {selectedDoc.first_name} {selectedDoc.last_name}
                            </h2>
                            <span className={`px-1.5 py-0.2 rounded text-[9px] font-mono font-bold ${
                              selectedDoc.doc_type === 'QPR'
                                ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                : 'bg-sky-50 text-sky-800 border border-sky-200'
                            }`}>
                              {selectedDoc.doc_type || 'SOAP'}
                            </span>
                          </div>

                          <div className="flex items-center justify-center flex-wrap gap-1.5 text-[11px] font-mono text-slate-600">
                            <span className={`px-2 py-0.5 rounded text-[9.5px] font-mono font-bold ${
                              isApproved
                                ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                : isReturned
                                ? 'bg-rose-50 text-rose-800 border border-rose-200'
                                : 'bg-amber-50 text-amber-800 border border-amber-200'
                            }`}>
                              {isApproved ? 'APPROVED' : isReturned ? 'RETURNED' : 'AWAITING APPROVAL'}
                            </span>
                            <span>•</span>
                            <span>
                              Assistant: <strong className="text-slate-900 font-bold">{selectedDoc.provider_name}</strong>
                              {isSelectedDocSlpa && <span className="text-[9px] px-1 py-0.2 rounded ml-1 font-semibold bg-sky-50 text-sky-800 border border-sky-200">SLPA</span>}
                            </span>
                            <span>•</span>
                            {selectedDoc.doc_type === 'QPR' ? (
                              <span>Quarter: <strong className="text-slate-900">{selectedDoc.qpr_quarter || 'Quarterly'}</strong></span>
                            ) : (
                              <span>DOS: <strong className="text-slate-900">{selectedDoc.dos || selectedDoc.document_date}</strong></span>
                            )}
                            {docPeriod && (
                              <>
                                <span>•</span>
                                <span>Period: <strong className="text-slate-900">{docPeriod.label}</strong></span>
                              </>
                            )}
                          </div>
                        </div>
                      </>
                    );
                  })()}

                  {/* Row 2: Primary Command Actions Rail in a Separate Row (Left-justified on Desktop, Centered on Mobile) */}
                  <div className="px-3.5 py-2 sm:px-4 sm:py-2.5 bg-[#f8fafc] flex items-center justify-center sm:justify-start gap-2 sm:gap-3 shrink-0 w-full">
                    {/* Supervisor Actions */}
                    {canSuperviseSelectedDoc && (
                      <>
                        {/* Approve — green */}
                        <button
                          onClick={handleSupervisorApprove}
                          disabled={processingSupervision}
                          className="group relative inline-flex items-center justify-center space-x-2 px-4 py-2 text-xs sm:text-sm font-bold rounded-lg transition-all duration-150 disabled:opacity-50 disabled:pointer-events-none focus:outline-none shrink-0 cursor-pointer bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-600 shadow-xs active:scale-[0.98]"
                          title="Approve and co-sign document"
                        >
                          <Check className="w-4 h-4 shrink-0 text-white" />
                          <span>{processingSupervision ? 'Approving...' : 'Approve'}</span>
                        </button>

                        {/* Return — red/rose */}
                        <button
                          onClick={() => setShowReturnModal(true)}
                          disabled={processingSupervision}
                          className="group relative inline-flex items-center justify-center space-x-2 px-4 py-2 text-xs sm:text-sm font-bold rounded-lg transition-all duration-150 disabled:opacity-50 disabled:pointer-events-none focus:outline-none shrink-0 cursor-pointer bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 shadow-xs active:scale-[0.98]"
                          title="Return document to clinician for revision"
                        >
                          <X className="w-4 h-4 shrink-0 text-rose-600" />
                          <span>Return</span>
                        </button>
                      </>
                    )}

                    {/* Edit */}
                    {selectedDoc.doc_type !== 'QPR' && selectedDoc.status !== 'FINALIZED' && !isEditing && (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="group relative inline-flex items-center justify-center space-x-2 px-4 py-2 text-xs sm:text-sm font-bold rounded-lg transition-all duration-150 focus:outline-none shrink-0 cursor-pointer bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 shadow-xs active:scale-[0.98]"
                        title="Edit note inline"
                      >
                        <Edit2 className="w-4 h-4 shrink-0 text-slate-600" />
                        <span>Edit</span>
                      </button>
                    )}

                    {isEditing && (
                      <button
                        onClick={handleSaveRevisions}
                        disabled={savingEdit}
                        className="group relative inline-flex items-center justify-center space-x-2 px-4 py-2 text-xs sm:text-sm font-bold rounded-lg shadow-xs active:scale-[0.98] transition-all duration-150 disabled:opacity-50 focus:outline-none shrink-0 cursor-pointer bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <Check className="w-4 h-4 shrink-0 text-white" />
                        <span>{savingEdit ? 'Saving...' : 'Save & Resubmit'}</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Pay Period & Cutoff Status Banner */}
                <div className="mx-3.5 sm:mx-4 p-3 sm:p-3.5 rounded-xl border space-y-2 text-xs bg-slate-50 border-slate-200 text-slate-800 shadow-xs">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b pb-2 border-slate-200">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-emerald-600" />
                      <span className="text-slate-600">
                        Pay Period: <strong className="text-slate-900 font-mono">{selectedDoc.pay_period?.label || selectedPeriod.label}</strong>
                      </span>
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-amber-50 text-amber-900 border-amber-200">
                        Cutoff: {formatDisplayDate(selectedDoc.pay_period?.cutoffDate || selectedPeriod.cutoffDate)}
                      </span>
                    </div>

                    <span className={`text-[10px] font-mono px-2.5 py-0.5 rounded-full font-bold uppercase self-start sm:self-auto border ${
                      selectedDoc.is_cleared || selectedDoc.clearance_status === 'CLEARED'
                        ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                        : selectedDoc.clearance_status === 'NEEDS_CORRECTION' || selectedDoc.supervisor_approval_status === 'RETURNED'
                        ? 'bg-rose-50 text-rose-800 border border-rose-200'
                        : 'bg-amber-50 text-amber-800 border border-amber-200'
                    }`}>
                      {selectedDoc.is_cleared || selectedDoc.clearance_status === 'CLEARED'
                        ? '✓ Documentation Cleared'
                        : selectedDoc.clearance_status === 'NEEDS_CORRECTION' || selectedDoc.supervisor_approval_status === 'RETURNED'
                        ? 'Action Required: Revision Needed'
                        : 'Pending Signatures'}
                    </span>
                  </div>

                  {/* Checklist Breakdown */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 text-[10.5px] font-mono pt-0.5">
                    <div className={`p-1.5 sm:p-2 rounded-lg border flex items-center justify-between ${
                      selectedDoc.clinician_signature
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                        : 'bg-white border-slate-200 text-slate-700'
                    }`}>
                      <span>1. Clinician Sig:</span>
                      <strong>{selectedDoc.clinician_signature ? '✓ Signed' : 'Pending'}</strong>
                    </div>

                    <div className={`p-1.5 sm:p-2 rounded-lg border flex items-center justify-between ${
                      selectedDoc.doc_type === 'QPR'
                        ? 'bg-slate-50 border-slate-200 text-slate-500'
                        : selectedDoc.guardian_signature || selectedDoc.guardian_signed_at || selectedDoc.guardian_signature_status === 'WAIVED_DOCUMENTED'
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                        : selectedDoc.esign_sent_at
                        ? 'bg-sky-50 border-sky-200 text-sky-800'
                        : 'bg-amber-50 border-amber-200 text-amber-800'
                    }`}>
                      <span>2. Guardian Sig:</span>
                      <strong className="text-right">
                        {selectedDoc.doc_type === 'QPR'
                          ? 'N/A (Milestone)'
                          : selectedDoc.guardian_signature || selectedDoc.guardian_signed_at
                          ? `✓ Signed ${formatConciseDateTime(selectedDoc.guardian_signed_at || selectedDoc.esign_completed_at)}`
                          : selectedDoc.guardian_signature_status === 'WAIVED_DOCUMENTED'
                          ? '✓ Waived'
                          : selectedDoc.esign_sent_at
                          ? `Sent ${formatConciseDateTime(selectedDoc.esign_sent_at)}`
                          : 'Pending'}
                      </strong>
                    </div>

                    <div className={`p-1.5 sm:p-2 rounded-lg border flex items-center justify-between ${
                      !isSelectedDocSlpa
                        ? 'bg-slate-50 border-slate-200 text-slate-500'
                        : selectedDoc.supervisor_approval_status === 'APPROVED'
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                        : selectedDoc.supervisor_approval_status === 'RETURNED'
                        ? 'bg-rose-50 border-rose-200 text-rose-800'
                        : 'bg-amber-50 border-amber-200 text-amber-800'
                    }`}>
                      <span>3. Supervisor Co-Sign:</span>
                      <strong>
                        {!isSelectedDocSlpa
                          ? 'N/A (SLP)'
                          : selectedDoc.supervisor_approval_status === 'APPROVED'
                          ? '✓ Approved'
                          : selectedDoc.supervisor_approval_status === 'RETURNED'
                          ? 'Returned'
                          : 'Pending'}
                      </strong>
                    </div>
                  </div>
                </div>

                {/* Billing & Operational Identity Evidence Panel */}
                <div className="mx-3.5 sm:mx-4">
                  <BillingOperationalEvidencePanel
                    selectedDoc={selectedDoc}
                    userRole={user.role}
                    canonicalBillingIdentity={canonicalBillingIdentity}
                    lightTheme={true}
                  />
                </div>

              {/* Prominent Returned for Revision Warning */}
              {selectedDoc.supervisor_approval_status === 'RETURNED' && (
                <div className="mx-3.5 sm:mx-4 p-3.5 rounded-xl border space-y-2 bg-rose-50 border-rose-200 text-rose-900 shadow-xs">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 font-bold text-xs text-rose-800">
                      <AlertTriangle className="w-4 h-4 shrink-0 text-rose-600" />
                      <span>Returned by Supervisor for Revision</span>
                    </div>
                    {!isEditing && selectedDoc.doc_type !== 'QPR' && selectedDoc.status !== 'FINALIZED' && (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="px-2.5 py-1 rounded font-bold text-xs flex items-center space-x-1 cursor-pointer bg-rose-600 hover:bg-rose-700 text-white shadow-xs"
                      >
                        <Edit2 className="w-3 h-3" />
                        <span>Edit Now</span>
                      </button>
                    )}
                  </div>
                  <p className="text-xs p-2.5 rounded-lg border bg-white border-rose-200 text-rose-900">
                    "{selectedDoc.supervisor_return_comments || 'Please revise clinical sections before cutoff.'}"
                  </p>
                </div>
              )}

              {/* Guardian eSign Status & Dispatch Audit Section */}
              {selectedDoc.doc_type === 'QPR' ? (
                <div className="mx-3.5 sm:mx-4 p-3.5 rounded-xl border text-xs flex items-center space-x-2.5 bg-sky-50 border-sky-200 text-sky-900 shadow-xs">
                  <ShieldCheck className="w-4 h-4 shrink-0 text-sky-600" />
                  <div>
                    <span className="font-semibold text-slate-900">Milestone Clinical Progress Report: </span>
                    <span>Guardian eSign is not required for quarterly milestone documentation. Approval and clearance is established by Supervising SLP Co-Signature.</span>
                  </div>
                </div>
              ) : selectedDoc.guardian_signature_status === 'WAIVED_DOCUMENTED' ? (
                <div className="mx-3.5 sm:mx-4 p-3.5 rounded-xl border text-xs flex flex-wrap items-center justify-between gap-2 bg-sky-50 border-sky-200 text-sky-900 shadow-xs">
                  <div className="flex items-center space-x-2">
                    <ShieldCheck className="w-4 h-4 shrink-0 text-sky-600" />
                    <span className="font-semibold text-slate-900">
                      Guardian Signature: WAIVED / DOCUMENTED (Exemption on File)
                    </span>
                  </div>
                  <div className="text-[11px] font-mono text-sky-800">
                    Justification: {selectedDoc.guardian_relationship || 'Clinical/Parental exemption documented'} • {formatConciseDateTime(selectedDoc.guardian_signed_at || selectedDoc.updated_at)}
                  </div>
                </div>
              ) : (() => {
                const isSigned = Boolean(selectedDoc.guardian_signature || selectedDoc.guardian_signed_at);
                const isSent = Boolean(selectedDoc.esign_sent_at || selectedDoc.esign_envelope_id);
                const guardianWindow = assessGuardianPayWindow(selectedDoc);
                const isOverdue = !isSigned && guardianWindow.isLate;
                const latePay = guardianWindow.isLate;
                
                let priorDispatches: any[] = [];
                if (selectedDoc.esign_evidence_json) {
                  try {
                    const parsed = JSON.parse(selectedDoc.esign_evidence_json);
                    if (Array.isArray(parsed.dispatches)) {
                      priorDispatches = parsed.dispatches;
                    }
                  } catch (e) {
                    priorDispatches = [];
                  }
                }
                if (priorDispatches.length === 0 && selectedDoc.esign_envelope_id) {
                  priorDispatches.push({
                    documentId: selectedDoc.id,
                    envelopeId: selectedDoc.esign_envelope_id,
                    sentAt: selectedDoc.esign_sent_at || '',
                    recipientName: selectedDoc.esign_signer_name || 'Guardian',
                    recipientEmail: selectedDoc.esign_signer_email || '',
                    recipientRole: selectedDoc.esign_signer_role || 'GUARDIAN',
                    senderUserId: 'Staff',
                    status: selectedDoc.esign_status || (isSigned ? 'COMPLETED' : 'SENT'),
                    completedAt: selectedDoc.esign_completed_at || selectedDoc.guardian_signed_at || null,
                    isActive: true
                  });
                }

                return (
                  <div className={`mx-3.5 sm:mx-4 p-3.5 rounded-xl border text-xs space-y-2.5 ${
                    isSigned
                      ? 'bg-emerald-50/70 border-emerald-200 text-emerald-900 shadow-xs'
                      : isOverdue
                      ? 'bg-rose-50/70 border-rose-200 text-rose-900 shadow-xs'
                      : isSent
                      ? 'bg-sky-50/70 border-sky-200 text-sky-900 shadow-xs'
                      : 'bg-slate-50 border-slate-200 text-slate-800 shadow-xs'
                  }`}>
                    {/* Primary concise status row */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center space-x-2">
                        {isSigned ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                        ) : isOverdue ? (
                          <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                        ) : isSent ? (
                          <Clock className="w-4 h-4 text-sky-600 shrink-0" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                        )}
                        <span className="font-semibold text-xs tracking-wide text-slate-900">
                          {isSigned
                            ? `Guardian Signature — Signed by ${selectedDoc.guardian_signature || 'Guardian'} (${selectedDoc.guardian_relationship || 'Parent/Guardian'}) on ${formatConciseDateTime(selectedDoc.guardian_signed_at || selectedDoc.esign_completed_at)}`
                            : guardianWindow.missing
                            ? GUARDIAN_CLOCK_MISSING_MESSAGE
                            : isOverdue
                            ? GUARDIAN_OVERDUE_PAY_MESSAGE
                            : guardianWindow.deadlineLabel
                            ? `Guardian acknowledgment due by ${guardianWindow.deadlineLabel}`
                            : isSent
                            ? `Guardian Signature — Sent ${formatConciseDateTime(selectedDoc.esign_sent_at)}`
                            : 'Guardian Signature — Parent/Guardian eSign Required'}
                        </span>
                      </div>
                      {isSigned && latePay && (
                        <div className="text-[11px] font-semibold text-rose-800">{GUARDIAN_OVERDUE_PAY_MESSAGE}</div>
                      )}

                      <div className="flex items-center space-x-2 shrink-0">
                        {priorDispatches.length > 0 && (
                          <button
                            onClick={() => setShowAuditTrail(!showAuditTrail)}
                            className="px-2 py-1 rounded-lg text-[11px] flex items-center space-x-1 shadow-xs cursor-pointer transition-colors bg-white hover:bg-slate-100 text-slate-700 border border-slate-200"
                          >
                            <span>{showAuditTrail ? 'Hide Audit Trail' : `Audit Trail (${priorDispatches.length})`}</span>
                          </button>
                        )}

                        {!isSigned && (
                          selectedDoc.doc_type === 'SOAP' && !soapGuardianEsignAccess(selectedDoc).allowed ? (
                            <span
                              className="px-2.5 py-1 rounded-lg text-[10px] font-medium border bg-slate-100 border-slate-200 text-slate-600"
                              title="Routine treatment SOAPs require in-person signature in Mobile SOAP. Remote SignWell dispatch requires Office/Admin exception approval."
                            >
                              In-Person Signature Standard
                            </span>
                          ) : (
                            <button
                              onClick={handleSendGuardianEsign}
                              disabled={sendingEsign}
                              className={`px-3 py-1 rounded-lg font-semibold text-[11px] flex items-center space-x-1 shadow-xs transition-colors cursor-pointer ${
                                isSent
                                  ? 'bg-sky-600 hover:bg-sky-700 text-white'
                                  : 'bg-amber-600 hover:bg-amber-700 text-white'
                              }`}
                              title={isSent ? 'Resending does not move the deadline. It stays 24 hours after the Arizona session end.' : 'Send SignWell eSign'}
                            >
                              <Send className="w-3 h-3" />
                              <span>{sendingEsign ? 'Dispatching...' : isSent ? 'Resend Envelope' : 'Send SignWell eSign'}</span>
                            </button>
                          )
                        )}
                      </div>
                    </div>

                    {/* Dispatch failure warning if present */}
                    {selectedDoc.esign_error && (
                      <div className="p-2 rounded-lg text-[11px] border bg-rose-50 border-rose-200 text-rose-800">
                        <span className="font-bold">Dispatch Delivery Issue: </span>
                        <span>{selectedDoc.esign_error} (Verify guardian email address in Client Profile)</span>
                      </div>
                    )}

                    {/* Dispatch Audit Trail Detail View */}
                    {showAuditTrail && priorDispatches.length > 0 && (
                      <div className="mt-2 pt-2.5 border-t border-slate-200 space-y-2 text-[11px]">
                        <div className="text-[10px] uppercase font-bold tracking-wider text-slate-600">
                          eSignature Dispatch Audit Trail & Chain of Custody
                        </div>
                        <div className="space-y-1.5">
                          {priorDispatches.map((disp: any, idx: number) => {
                            const isActiveEnvelope = idx === 0 && disp.status !== 'SUPERSEDED';
                            const isDeliveryFailure = disp.status === 'BOUNCED' || disp.status === 'FAILED';
                            return (
                              <div
                                key={disp.envelopeId || idx}
                                className={`p-2 rounded-lg border flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 ${
                                  isDeliveryFailure
                                    ? 'bg-rose-50 border-rose-200 text-rose-900'
                                    : isActiveEnvelope
                                    ? 'bg-white border-sky-300 text-slate-800 shadow-xs'
                                    : 'bg-slate-50 border-slate-200 text-slate-600'
                                }`}
                              >
                                <div className="space-y-0.5">
                                  <div className="flex items-center space-x-2">
                                    <span className={`px-1.5 py-0.2 rounded text-[9px] font-bold uppercase tracking-wider ${
                                      isDeliveryFailure
                                        ? 'bg-rose-100 text-rose-800 border border-rose-200'
                                        : isActiveEnvelope
                                        ? 'bg-sky-100 text-sky-800 border border-sky-200'
                                        : 'bg-slate-100 text-slate-700 border border-slate-200'
                                    }`}>
                                      {isDeliveryFailure ? 'DELIVERY FAILED' : isActiveEnvelope ? 'ACTIVE ENVELOPE' : 'SUPERSEDED'}
                                    </span>
                                    <span className="font-mono text-[10px] text-slate-700">
                                      ID: {disp.envelopeId || 'N/A'}
                                    </span>
                                    <span className="text-[10px] text-slate-500">
                                      Doc: {disp.documentId || selectedDoc.id}
                                    </span>
                                  </div>
                                  <div className="text-[10px] text-slate-700">
                                    Recipient: <span className="font-semibold text-slate-900">{disp.recipientName || 'Parent / Guardian'}</span> ({disp.recipientEmail || 'No email'}) • Sender: <span className="text-slate-800">{disp.senderUserId || 'Staff'}</span>
                                  </div>
                                  {disp.errorMessage && (
                                    <div className="text-[10px] text-rose-600 italic">
                                      Failure Reason: {disp.errorMessage}
                                    </div>
                                  )}
                                </div>

                                <div className="text-right text-[10px] space-y-0.5 shrink-0">
                                  <div>
                                    Sent: <span className="font-mono text-slate-700">{formatConciseDateTime(disp.sentAt) || 'Recorded'}</span>
                                  </div>
                                  <div>
                                    Status: <span className={`font-semibold ${disp.status === 'COMPLETED' ? 'text-emerald-700' : isDeliveryFailure ? 'text-rose-700' : disp.status === 'SUPERSEDED' ? 'text-slate-500' : 'text-sky-700'}`}>{disp.status}</span>
                                    {disp.completedAt && (
                                      <span className="ml-1 text-emerald-700">({formatConciseDateTime(disp.completedAt)})</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="text-[10px] italic text-slate-500">
                          Notice: Sending or resending does not move the deadline. It remains 24 hours after the session end in Arizona time.
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Document Body: Branch between QPR Milestone Structured Display and Treatment Note SOAP Sections */}
              {selectedDoc.doc_type === 'QPR' ? (
                <div className="mx-3.5 sm:mx-4 p-3 sm:p-3.5 rounded-xl border border-slate-200/90 space-y-3 bg-white shadow-xs">
                  <div className="flex items-center justify-between border-b pb-2 border-slate-200">
                    <div className="text-xs font-bold flex items-center space-x-1.5 text-slate-900">
                      <FileCheck2 className="w-4 h-4 text-amber-600" />
                      <span>Quarterly Progress Report (DDD-2063A)</span>
                    </div>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full font-semibold bg-amber-50 text-amber-900 border border-amber-200">
                      Quarter: {selectedDoc.qpr_quarter || 'Q1 2026'}
                    </span>
                  </div>

                  {sectionData && Object.keys(sectionData).length > 0 ? (
                    <ClinicalDocStructuredDisplay doc={selectedDoc} sectionData={sectionData} lightTheme={true} />
                  ) : (
                    <div className="p-5 text-center text-xs rounded-xl border shadow-xs bg-slate-50 border-slate-200 text-slate-700">
                      <p className="font-semibold text-slate-900">Quarterly Progress Report Details</p>
                      <p className="mt-1 text-slate-600">{selectedDoc.title || 'DDD-2063A Quarterly Progress Review'}</p>
                      {selectedDoc.objective && (
                        <div className="mt-2.5 text-left p-2.5 rounded border whitespace-pre-wrap bg-white border-slate-200 text-slate-900">
                          {selectedDoc.objective}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : selectedDoc.doc_type === 'SOAP' ? (
                <div className="mx-3.5 sm:mx-4">
                  <DailyTreatmentNoteBody
                    doc={selectedDoc}
                    light
                    editing={isEditing ? {
                      narrative: dailyNarrative,
                      goals: dailyGoals,
                      observations: dailyObservations,
                      onNarrative: setDailyNarrative,
                      onGoals: setDailyGoals,
                      onObservations: setDailyObservations
                    } : undefined}
                  />
                </div>
              ) : (
                /* Treatment Note Body (SOAP Sections) */
                <div className="mx-3.5 sm:mx-4 space-y-3">
                  {/* Subjective */}
                  <div className="space-y-1.5">
                    <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-800">
                      Subjective (S)
                    </div>
                    {isEditing ? (
                      <textarea
                        value={editSubjective}
                        onChange={(e) => setEditSubjective(e.target.value)}
                        rows={3}
                        className="w-full border rounded-xl p-2.5 text-xs focus:outline-none shadow-xs bg-slate-50 border-slate-300 text-slate-900 focus:border-slate-400 focus:ring-1 focus:ring-slate-300"
                      />
                    ) : (
                      <div className="p-3 rounded-xl border text-xs leading-relaxed shadow-xs bg-white border-slate-200/90 text-slate-800">
                        {selectedDoc.subjective || 'No subjective statement recorded.'}
                      </div>
                    )}
                  </div>

                  {/* Objective */}
                  <div className="space-y-1.5">
                    <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-800">
                      Objective (O)
                    </div>
                    {isEditing ? (
                      <textarea
                        value={editObjective}
                        onChange={(e) => setEditObjective(e.target.value)}
                        rows={3}
                        className="w-full border rounded-xl p-2.5 text-xs focus:outline-none shadow-xs bg-slate-50 border-slate-300 text-slate-900 focus:border-slate-400 focus:ring-1 focus:ring-slate-300"
                      />
                    ) : (
                      <div className="p-3 rounded-xl border text-xs leading-relaxed shadow-xs bg-white border-slate-200/90 text-slate-800">
                        {selectedDoc.objective || 'No objective clinical data recorded.'}
                      </div>
                    )}
                  </div>

                  {/* Assessment */}
                  <div className="space-y-1.5">
                    <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-800">
                      Assessment (A)
                    </div>
                    {isEditing ? (
                      <textarea
                        value={editAssessment}
                        onChange={(e) => setEditAssessment(e.target.value)}
                        rows={3}
                        className="w-full border rounded-xl p-2.5 text-xs focus:outline-none shadow-xs bg-slate-50 border-slate-300 text-slate-900 focus:border-slate-400 focus:ring-1 focus:ring-slate-300"
                      />
                    ) : (
                      <div className="p-3 rounded-xl border text-xs leading-relaxed shadow-xs bg-white border-slate-200/90 text-slate-800">
                        {selectedDoc.assessment || 'No clinical assessment recorded.'}
                      </div>
                    )}
                  </div>

                  {/* Plan */}
                  <div className="space-y-1.5">
                    <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-800">
                      Plan (P)
                    </div>
                    {isEditing ? (
                      <textarea
                        value={editPlan}
                        onChange={(e) => setEditPlan(e.target.value)}
                        rows={2}
                        className="w-full border rounded-xl p-2.5 text-xs focus:outline-none shadow-xs bg-slate-50 border-slate-300 text-slate-900 focus:border-slate-400 focus:ring-1 focus:ring-slate-300"
                      />
                    ) : (
                      <div className="p-3 rounded-xl border text-xs leading-relaxed shadow-xs bg-white border-slate-200/90 text-slate-800">
                        {selectedDoc.plan || 'Plan to continue established POC goals.'}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Signatures & Execution Block */}
              <div className="mx-3.5 sm:mx-4 pt-2.5 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-mono">
                <div className="p-2.5 rounded-xl border border-slate-200 space-y-1 bg-slate-50 shadow-xs">
                  <div className="text-[9.5px] uppercase font-bold tracking-wider text-slate-500">Treating Clinician</div>
                  <div className={selectedDoc.clinician_signature ? 'text-emerald-700 font-semibold text-xs' : 'text-slate-800 font-semibold text-xs'}>
                    {selectedDoc.clinician_signature || 'Unsigned'}
                  </div>
                  <div className="text-[9.5px] font-mono text-slate-500">{selectedDoc.clinician_signed_at || 'Pending'}</div>
                </div>

                <div className="p-2.5 rounded-xl border border-slate-200 space-y-1 bg-slate-50 shadow-xs">
                  <div className="text-[9.5px] uppercase font-bold tracking-wider text-slate-500">Supervising SLP Co-Signature</div>
                  <div className={selectedDoc.supervisor_approval_status === 'APPROVED' ? 'text-emerald-700 font-semibold text-xs' : 'text-slate-800 font-semibold text-xs'}>
                    {!isSelectedDocSlpa ? 'N/A — Licensed SLP' : selectedDoc.supervisor_approval_status === 'APPROVED' ? (selectedDoc.supervisor_signature || 'Co-Signed & Approved') : 'Awaiting Co-Sign'}
                  </div>
                  <div className="text-[9.5px] font-mono text-slate-500">{selectedDoc.supervisor_signed_at || (isSelectedDocSlpa ? 'Pending' : 'N/A')}</div>
                </div>
              </div>
            </div>
          </>
          ) : (
            <div className="p-12 text-center rounded-xl border text-xs bg-white border-slate-200 text-slate-500 shadow-xs">
              Select a treatment note on the left to view documentation details and pre-cutoff status.
            </div>
          )}
        </div>
      </div>

      {/* Return for Revision Modal */}
      {showReturnModal && selectedDoc && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="border border-slate-300 rounded-xl max-w-lg w-full p-5 space-y-4 shadow-xl bg-white text-slate-900">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div className="flex items-center space-x-2 font-bold text-sm text-rose-700">
                <AlertTriangle className="w-5 h-5 shrink-0" />
                <span>{selectedDoc.doc_type === 'QPR' ? 'Return Quarterly Progress Report for Revision' : 'Return Treatment Note for Revision'}</span>
              </div>
              <button
                onClick={() => setShowReturnModal(false)}
                className="text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-slate-600">
              Please enter specific instructions for <strong className="text-slate-900">{selectedDoc.provider_name}</strong> regarding required clinical revisions {selectedDoc.doc_type === 'QPR' ? 'for this milestone report' : `before Friday's cutoff (${formatDisplayDate(selectedPeriod.cutoffDate)})`}:
            </p>

            <textarea
              value={returnComments}
              onChange={(e) => setReturnComments(e.target.value)}
              rows={4}
              placeholder="e.g. Please clarify percentage accuracy achieved for Goal 2 and add subjective response to therapy..."
              className="w-full rounded-lg p-3 text-xs focus:outline-none focus:border-rose-500 bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400"
            />

            <div className="flex items-center justify-end space-x-3 pt-2">
              <button
                onClick={() => setShowReturnModal(false)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSupervisorReturn}
                disabled={processingSupervision || !returnComments.trim()}
                className="px-4 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold shadow-sm transition-colors cursor-pointer"
              >
                {processingSupervision ? 'Returning...' : 'Confirm & Return to Clinician'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Clinical Document / SOAP Modal */}
      {showNewDocModal && (
        <NewClinicalDocModal
          isOpen={showNewDocModal}
          onClose={() => setShowNewDocModal(false)}
          onSuccess={async (newDocId) => {
            setShowNewDocModal(false);
            await fetchDocs(newDocId);
          }}
          user={user}
        />
      )}
    </div>
  );
};
